import windowSize from "../services/WindowSize.js";

const MOBILE_FILTER_BREAKPOINT = 768;

/**
 * Filters posts through the REST API and keeps the filter UI in sync.
 *
 * @package AGDS
 * @category Modules
 * @module PostsFilter
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class PostsFilter {
    /**
     * Creates a posts filter instance.
     *
     * @param {Object} options
     * @param {ApiService} options.api
     * @param {HTMLElement} [options.preloader]
     * @param {PostsLoader} options.postsLoader
     * @param {HTMLElement} options.postsContainer
     * @param {HTMLElement} options.filterContainer
     */
    constructor(options = {}) {
        this.preloader = options.preloader;
        this.api = options.api;
        this.spollers = options.spollers;
        this.filterContainer = options.filterContainer;
        this.postsContainer = options.postsContainer;
        this.postsLoader = options.postsLoader;

        this._selectInitDone = false;
        this.loadMoreBtn = this.postsContainer?.parentElement.querySelector(".loadmore-btn");
        this.postType = this.postsContainer?.dataset.postType;
    }

    /**
     * Initializes the filter module.
     *
     * @returns {void}
     */
    init() {
        if (!this.filterContainer || !this.postsContainer) return;

        this.selectListInit();
        this.setupFilter();
        this.checkUrlParams();
    }

    /**
     * Binds filter click handlers.
     *
     * @returns {void}
     */
    setupFilter() {
        this.filterContainer.addEventListener("click", (e) => {
            const option = e.target.closest(".dropdown-item");
            const button = e.target.closest(".posts-filter--button");

            if (option) {
                this.handleFilterClick(option);
                this.updateMobileDropdown(option);
                return;
            }

            if (button) {
                const btnWrap = button.closest(".posts-filter");
                if (!btnWrap) return;

                const isOpen = btnWrap.classList.toggle("_open");
                button.setAttribute("aria-expanded", String(isOpen));
                return;
            }
        });
    }

    /**
     * Handles a filter option click.
     *
     * @async
     * @param {HTMLElement} option - Selected filter option.
     * @returns {void}
     */
    async handleFilterClick(option) {
        this.filterContainer.querySelectorAll(".dropdown-item").forEach((el) => el.classList.remove("_active"));
        option.classList.add("_active");

        const termId = option.dataset.termId;
        const taxonomy = this.filterContainer.dataset.taxonomy;

        const filters =
            termId && termId !== "0" && termId !== "all"
                ? {
                      tax_query: [
                          {
                              taxonomy: taxonomy,
                              field: "term_id",
                              terms: Number(termId),
                          },
                      ],
                  }
                : { tax_query: [] };

        if (this.loadMoreBtn) {
            this.loadMoreBtn.dataset.termId = termId;
        }

        this.filterPosts(filters);
    }

    /**
     * Initializes dropdown close behavior.
     *
     * @returns {void}
     */
    selectListInit() {
        if (this._selectInitDone) return;
        this._selectInitDone = true;

        document.addEventListener("click", (e) => {
            if (!e.target.closest(".posts-filter")) {
                document.querySelectorAll(".posts-filter._open").forEach((wrap) => {
                    wrap.classList.remove("_open");
                });
            }
        });
    }

    /**
     * Updates the mobile dropdown label.
     *
     * @param {HTMLElement} option - Selected dropdown option.
     * @returns {void}
     */
    updateMobileDropdown(option) {
        if (!windowSize.isBelow(MOBILE_FILTER_BREAKPOINT)) return;
        if (!option) return;

        const filterBlock = option.closest(".posts-filter");
        if (!filterBlock) return;

        const button = filterBlock.querySelector(".posts-filter--button");
        const buttonTitle = button?.querySelector("span");
        const itemTitle = option.querySelector("span");

        if (buttonTitle && itemTitle) {
            buttonTitle.textContent = itemTitle.textContent;
        }

        filterBlock.classList.remove("_open");
    }

    /**
     * Filters posts through the REST API.
     *
     * @async
     * @param {Object} filters - Filter parameters.
     * @returns {void}
     */
    async filterPosts(filters) {
        try {
            this.togglePreloader(true);

            const response = await this.api.postsFilter(this.postType, 1, filters);

            if (response.data.html) {
                this.postsContainer.innerHTML = response.data.html;

                await this.postsLoader.waitForImages(this.postsContainer);

                if (this.postsLoader) {
                    this.postsLoader.reset(filters, response.data.has_more);
                }

                if (this.spollers) {
                    this.spollers.init();
                }
            }
        } catch (error) {
            console.error("FilterPost Error:", error);
        } finally {
            this.togglePreloader(false);
        }
    }

    /**
     * Toggles the loading indicator.
     *
     * @param {boolean} show - Whether the preloader should be visible.
     * @returns {void}
     */
    togglePreloader(show) {
        if (show) {
            this.preloader?.classList.add("_show");
        } else {
            this.preloader?.classList.remove("_show");
        }
    }

    /**
     * Applies filter state from URL parameters.
     *
     * @returns {void}
     */
    checkUrlParams() {
        const urlParams = new URLSearchParams(window.location.search);
        const catID = urlParams.get("cat") || urlParams.get("category");

        if (catID && this.filterContainer) {
            const item = this.filterContainer.querySelector(`.dropdown-item[data-term-id="${catID}"]`);
            if (item) {
                this.handleFilterClick(item);
            }
        }
    }
}
